/**
 * PerplexityContentRenderer - CONTENT COMPONENT (MINIMAL RESPONSIVE LOGIC)
 * 
 * This component renders Perplexity research content within TextContentLightbox.
 * 
 * ARCHITECTURE RULES:
 * - Avoid responsive breakpoints where possible - let TextContentLightbox handle layout
 * - Only use responsive classes for content-specific needs (text size, source button sizing)
 * - All layout and container responsive behavior is handled by TextContentLightbox (master)
 * 
 * RESPONSIVE STRATEGY:
 * - Uses lg: breakpoint to align with master component's 1024px strategy
 * - Minimal responsive classes for content readability only
 * - Does NOT control layout, padding, or container behavior
 * 
 * If you need layout changes, modify TextContentLightbox, not this file!
 */
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ExternalLink } from 'lucide-react';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';

import GlassPill from '@/components/ui/GlassPill';
import { TextContentContext } from '@/components/text-content-lightbox/types/TextContentLightbox';
import { PerplexityContent, PerplexitySource } from '../types/PerplexityContent';
import { useToggleResearchFavorite } from '@/hooks/research-generator/library/use-toggle-research-favorite';
import { getShortCategoryName } from '@/utils/research-category-utils';

interface PerplexityContentRendererProps {
  content: PerplexityContent;
  context: TextContentContext<PerplexityContent>;
  onSourceClick?: (source: PerplexitySource) => void;
  showFavoriteButton?: boolean; // NEW: Optional prop to hide favorite button
}

export const PerplexityContentRenderer: React.FC<PerplexityContentRendererProps> = ({
  content,
  context,
  onSourceClick,
  showFavoriteButton = true // Default to true for backward compatibility
}) => {
  const toggleFavoriteMutation = useToggleResearchFavorite();
  const [isFavorited, setIsFavorited] = useState(content.metadata?.parameters?.isFavorited || false);
  const [favoriteCount, setFavoriteCount] = useState(content.metadata?.parameters?.favoriteCount || 0);

  // Check and update favorite status from metadata
  useEffect(() => {
    if (content.metadata?.parameters) {
      setIsFavorited(content.metadata.parameters.isFavorited || false);
      setFavoriteCount(content.metadata.parameters.favoriteCount || 0);
    }
  }, [content.metadata]);

  const handleToggleFavorite = async (contentId: string): Promise<void> => {
    try {
      await toggleFavoriteMutation.mutateAsync({
        researchId: contentId,
        isFavorited,
      });
      
      // Optimistic update
      setIsFavorited(!isFavorited);
      setFavoriteCount(prev => isFavorited ? prev - 1 : prev + 1);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleSourceClick = (source: PerplexitySource) => {
    if (onSourceClick) {
      onSourceClick(source);
    } else {
      // Default behavior: open in new tab
      window.open(source.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCitationClick = (citationNumber: number) => {
    if (content.sources && citationNumber <= content.sources.length) {
      const source = content.sources[citationNumber - 1];
      if (source) {
        handleSourceClick(source);
      }
    }
  };

  // Preprocess content to convert citations [1], [2], etc. into markdown links
  const preprocessContent = (contentText: string): string => {
    if (!contentText || !content.sources || content.sources.length === 0) {
      return contentText;
    }

    // Replace citation patterns [1], [2], etc. with markdown links
    return contentText.replace(/\[(\d+)\]/g, (match, citationNumber) => {
      const index = parseInt(citationNumber) - 1;
      if (index >= 0 && index < content.sources.length) {
        const source = content.sources[index];
        return `[${citationNumber}](${source.url})`;
      }
      return match; // Return original if no matching source
    });
  };

  const processedContent = preprocessContent(content.content);

  // Truncate source title helper
  const truncateSourceTitle = (title: string, maxLength: number = 40) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  return (
    <div className="relative space-y-6 min-w-0 w-full max-w-full">
        {/* Modern Glass Pill Header */}
        <div className="space-y-4">
          {/* Reorganized Header Layout */}
          <div className="flex flex-wrap items-center justify-between gap-2 w-full">
            {/* Left side - Page Number + Favorites Button */}
            <div className="flex items-center gap-2">
              {/* Page Number - FIRST */}
              {context.totalItems > 1 && (
                <GlassPill 
                  type={`${context.currentIndex + 1} of ${context.totalItems}`}
                  variant="default"
                  className="text-sm font-medium"
                />
              )}
              
              {/* If only one item, still show page number for consistency */}
              {context.totalItems <= 1 && (
                <GlassPill 
                  type="1 of 1"
                  variant="default"
                  className="text-sm font-medium"
                />
              )}
              
              {/* Favorites Button - SECOND - only show if enabled */}
              {showFavoriteButton && (
                <PersistentFavoriteButton
                  itemId={content.id}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorite_prop={isFavorited}
                  favoriteCount_prop={favoriteCount}
                />
              )}
            </div>
            
            {/* Right side - Category + Date */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Category - FIRST on right */}
              <GlassPill 
                type={getShortCategoryName(content.metadata?.researchType || content.contentType)}
                variant="amber"
                className="text-sm font-medium"
              />
              
              {/* Research Date - SECOND on right */}
              {content.metadata?.createdAt && (
                <GlassPill 
                  type={new Date(content.metadata.createdAt).toLocaleDateString()}
                  variant="blue"
                  className="text-sm font-medium"
                />
              )}
            </div>
          </div>
          
          {/* Title - lg: breakpoint aligns with master TextContentLightbox strategy */}
          <h1 className="text-xl lg:text-2xl font-semibold text-foreground leading-tight pr-2 break-words">
            {content.title}
          </h1>
        </div>

        {/* Main Content with Citation Processing - PROPER CONTAINMENT */}
        <div className="relative w-full max-w-full min-w-0 prose prose-gray dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-blockquote:text-foreground prose-code:text-foreground prose-pre:bg-muted prose-li:text-foreground max-w-none">
          <style>{`
            .prose {
              word-wrap: break-word;
              overflow-wrap: break-word;
              hyphens: auto;
              max-width: 100% !important;
            }
            
            .prose p {
              margin-bottom: 1rem;
              line-height: 1.7;
              text-align: justify;
              word-spacing: 0.1em;
            }
            
            .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
              font-weight: 600;
              line-height: 1.3;
            }
            
            .prose h1 { font-size: 1.5rem; }
            .prose h2 { font-size: 1.25rem; }
            .prose h3 { font-size: 1.125rem; }
            
            .prose ul, .prose ol {
              margin: 1rem 0;
              padding-left: 1.5rem;
            }
            
            .prose li {
              margin: 0.5rem 0;
              line-height: 1.6;
            }
            
            .prose blockquote {
              border-left: 4px solid #e5e7eb;
              padding-left: 1rem;
              margin: 1.5rem 0;
              font-style: italic;
              color: #6b7280;
            }
            
            .prose code {
              background-color: #f3f4f6;
              padding: 0.125rem 0.25rem;
              border-radius: 0.25rem;
              font-size: 0.875em;
            }
            
            .prose pre {
              background-color: #f9fafb;
              padding: 1rem;
              border-radius: 0.5rem;
              overflow-x: auto;
              border: 1px solid #e5e7eb;
            }
            
            .prose a {
              text-decoration: underline;
              text-decoration-thickness: 1px;
              text-underline-offset: 2px;
            }
            
            .prose a:hover {
              text-decoration-thickness: 2px;
            }
            
            /* Glass pill styling for numbered citation links */
            .prose a[href*="http"]:not([href*="#"]) {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-width: 20px;
              height: 18px;
              padding: 2px 6px;
              margin: 0 2px;
              background: linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(6, 182, 212, 0.1));
              border: 1px solid rgba(59, 130, 246, 0.3);
              border-radius: 12px;
              color: rgb(59, 130, 246);
              text-decoration: none;
              font-size: 11px;
              font-weight: 600;
              transition: all 0.2s ease;
              word-break: normal;
              flex-shrink: 0;
              backdrop-filter: blur(4px);
            }
            
            .prose a[href*="http"]:not([href*="#"]):hover {
              background: linear-gradient(to right, rgba(59, 130, 246, 0.2), rgba(6, 182, 212, 0.2));
              border-color: rgba(59, 130, 246, 0.5);
              transform: scale(1.05);
              box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
            }
            
            .dark .prose a[href*="http"]:not([href*="#"]) {
              background: linear-gradient(to right, rgba(59, 130, 246, 0.2), rgba(6, 182, 212, 0.2));
              border-color: rgba(59, 130, 246, 0.4);
              color: rgb(147, 197, 253);
            }
            
            .dark .prose a[href*="http"]:not([href*="#"]):hover {
              background: linear-gradient(to right, rgba(59, 130, 246, 0.3), rgba(6, 182, 212, 0.3));
              border-color: rgba(59, 130, 246, 0.6);
            }
            
            .prose table {
              width: 100%;
              border-collapse: collapse;
              margin: 1rem 0;
            }
            
            .prose th, .prose td {
              border: 1px solid #e5e7eb;
              padding: 0.5rem;
              text-align: left;
            }
            
            .prose th {
              background-color: #f9fafb;
              font-weight: 600;
            }
`}
          </style>
          
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children, ...props }) => {
                // Handle citation links
                if (href && href.startsWith('http') && typeof children === 'string' && /^\d+$/.test(children)) {
                  const citationNumber = parseInt(children);
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        handleCitationClick(citationNumber);
                        window.open(href, '_blank', 'noopener,noreferrer');
                      }}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                }
                // Handle regular links
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                );
              }
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </div>

        {/* Sources Section - Moved to Bottom */}
        {content.sources && content.sources.length > 0 && (
          <div className="border-t border-border/50 pt-6 mt-8">
            <h3 className="text-lg font-semibold text-foreground mb-4">Sources</h3>
            <div className="flex flex-wrap gap-2 w-full overflow-hidden">
              {content.sources.map((source, index) => (
                <button
                  key={source.url}
                  onClick={() => handleSourceClick(source)}
                  // Source button sizing - lg: breakpoint aligns with master component strategy
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 dark:from-blue-400/20 dark:to-cyan-400/20 backdrop-blur-sm border border-blue-200/30 dark:border-blue-400/30 rounded-full text-xs font-medium text-blue-700 dark:text-blue-300 hover:from-blue-500/20 hover:to-cyan-500/20 dark:hover:from-blue-400/30 dark:hover:to-cyan-400/30 transition-all duration-200 group cursor-pointer max-w-[280px] lg:max-w-[320px]"
                  title={source.url}
                >
                  <span className="font-semibold flex-shrink-0">[{index + 1}]</span>
                  {/* Text truncation - sm: for mobile, lg: for desktop to align with master strategy */}
                  <span className="hidden sm:inline truncate max-w-[180px] lg:max-w-[220px]">
                    {truncateSourceTitle(source.title, 35)}
                  </span>
                  <span className="sm:hidden truncate max-w-[80px]">
                    {truncateSourceTitle(source.title, 15)}
                  </span>
                  <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}; 